import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { RouteComponentProps } from "react-router";
import { generatePath, withRouter } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import HealthConditionStore from "stores/HealthConditionStore";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { SNACKBAR_TYPES } from "components/Snackbar";
import ROUTES from "navigation/routes";
import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  CardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import HealthConditionCard from "./HealthConditionCard";
import { constants } from "./constants";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class HealthConditions extends React.Component<Props> {
  @computed get items() {
    return HealthConditionStore.items;
  }

  @computed get isListEmpty() {
    return this.items.length === 0;
  }

  componentDidMount() {
    HealthConditionStore.fetch();
  }

  componentWillUnmount() {
    HealthConditionStore.resetPage();
  }

  fetchMoreItems = async () => {
    const { setMessage } = this.props;

    try {
      await HealthConditionStore.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    HealthConditionStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && HealthConditionStore.fetch();
  };

  onSearchClear = () => {
    HealthConditionStore.clearSearch();
    HealthConditionStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    HealthConditionStore.applyOrder(value);
    HealthConditionStore.resetPage();
    HealthConditionStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      HealthConditionStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    HealthConditionStore.resetPage();
    HealthConditionStore.fetch();
  };

  onFilterClear = () => {
    HealthConditionStore.clearFilters();
    HealthConditionStore.fetch();
  };

  onAddItemClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (item: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = item;

    try {
      await HealthConditionStore.changeStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.healthConditions.title}
          controls={
            <>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddItemClick}
                >
                  {constants.healthConditions.addButtonTitle}
                </Button>
              </PageControl>
            </>
          }
          isLoading={HealthConditionStore.isLoading}
        >
          <SearchContainer>
            <Search
              initValue={HealthConditionStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={HealthConditionStore.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={HealthConditionStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </SearchContainer>

          {(HealthConditionStore.isPublishing ||
            HealthConditionStore.isCopying) && <OverflowLoader />}
          {!this.isListEmpty ? (
            <InfiniteScroll
              dataLength={this.items.length}
              hasMore={HealthConditionStore.hasMoreInfo}
              next={this.fetchMoreItems}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              {this.items.map(item => (
                <CardWrapper key={item.id}>
                  <HealthConditionCard
                    title={item.title}
                    image={item.imageUrl}
                    publishStatus={item.publishStatus}
                    onDetailsClick={this.onDetailsClick(item)}
                    onEdit={this.onEdit(item)}
                    onPublish={this.onPublish(item)}
                  />
                </CardWrapper>
              ))}
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                HealthConditionStore.search
                  ? constants.healthConditions.noRecordsMatch
                  : constants.healthConditions.noRecords
              }
            />
          )}
        </Page>
      </LayoutContainer>
    );
  }
}

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(HealthConditions)),
);
